import { parseObligation } from "@renec-foundation/relend-adapter"
import { PublicKey } from "@solana/web3.js"
import BigNumber from "bignumber.js"
import BN from "bn.js"
import { StaticImageData } from "next/image"
import React from "react"
import { BORROW, DEPOSIT, REPAY, STAKING, SUPPLY, UNSTAKING, WITHDRAW } from "./constants"

export type SelectItem = {
  value: string
  icon: string | StaticImageData | null
  iconName: string
  alt: string
  price?: BigNumber
  ltv?: number
  symbol?: string
}

export type PopupBorrowRepayState = {
  isOpen: boolean
  type: typeof BORROW | typeof REPAY
}

export type PopupSupplyWithdrawState = {
  isOpen: boolean
  type: typeof DEPOSIT | typeof WITHDRAW
}

export type PopupStakeUnstakeState = {
  isOpen: boolean
  type: typeof STAKING | typeof UNSTAKING
}

export type PopupClaimStakingState = {
  isOpen: boolean
}

export type PopupStopUnstakingState = {
  isOpen: boolean
}

export type PopupDevelopingState = {
  isOpen: boolean
}

export type PopupModalRewardState = {
  isOpen: boolean
}

export type RestakingLockupOptions = {
  duration: number
  minUnstakingAmount: number
}

export type RestakingVaultStat = {
  totalStakedAmount: number
  totalStakers: number
  emissionPerDay: number
}

export type RestakingRewardType = {
  rewardTokenDecimals: number
  rewardTokenMint: PublicKey
  timestamp: number
}

export type RestakingVaultState = {
  vaultOwner: PublicKey
  vaultTokenAccount: PublicKey
  allowedToken: PublicKey
  lockupOptions: RestakingLockupOptions[]
  rewards: RestakingRewardType[]
  vaultStat: RestakingVaultStat
} | null

export type RestakingBalancesState = {
  stakeToken: number
  rewardToken: number
}

export type RestakingAccountRewardType = {
  accumulatedRewardAmount: BN
  rewardRef: RestakingRewardType
}

export type RestakingAccountState = {
  owner: PublicKey
  vault: PublicKey
  stakedAmount: BN
  unstakedAmount: BN
  unlockTimestamp: BN
  lastStaked: BN
  rewards: RestakingAccountRewardType[]
}

export type ObligationReserveType = {
  liquidationThreshold: number
  loanToValueRatio: number
  symbol: string | undefined
  price: BigNumber
  reserveAddress: string
  amount: BigNumber
  amountUsd: BigNumber
}

export type TransactionDetail = {
  isActive: boolean
  ctaButtonLink?: string | null
  title: string
  content: React.ReactNode
  ctaButtonTitle?: React.ReactNode
  isDisplayingGoHomeButton?: boolean
  resultImage?: StaticImageData
  isError?: boolean
}

export type HelperText = {
  content: React.ReactNode
  color: string
}

export type RewardData = {
  rewardAmount: number
  lastUpdated: number
  exponent: number
  lastClaimed: number
  obligationId: string
  owner: string
}

export type RewardConfigData = {
  lockDuration: number
}

export type ObligationBorrowReserve = {
  liquidationThreshold: number
  loanToValueRatio: number
  symbol: string | undefined
  price: BigNumber
  cumulativeBorrowRate: BigNumber
  reserveAddress: string
  amount: BigNumber
  amountUsd: BigNumber
  weightedAmountUsd: BigNumber
  borrowedAmountDecimals: BigNumber
}

export type ObligationDepositReserve = {
  liquidationThreshold: number
  loanToValueRatio: number
  symbol: string | undefined
  price: BigNumber
  reserveAddress: string
  amount: BigNumber
  amountUsd: BigNumber
  depositedAmountDecimals: BigNumber
  earnedAmount: BigNumber
}

export type ParsedTokenAccountData = {
  parsed: {
    info: {
      isNative: boolean
      mint: string
      owner: string
      state: string
      tokenAmount: {
        amount: string
        decimals: number
        uiAmount: number
        uiAmountString: string
      }
    }
    type: string
    program: string
    space: number
  }
  isSucceeded: boolean
  mintAddress: string
  tokenBalance: number
}

export type ParsedObligationType = Awaited<ReturnType<typeof parseObligation>>

export type CTokenExchangeSymbolType =
  | undefined
  | {
      [key: string]: BigNumber[]
    }

export type MuiStyles = { [key: string]: string | number }

export type GrowthHackingTokenNameType = typeof SUPPLY | typeof BORROW
export type GrowthHackingTabType = typeof DEPOSIT | typeof WITHDRAW | typeof BORROW | typeof REPAY

export type GrowthHackingState = {
  isOpen: boolean
  type: GrowthHackingTokenNameType
  tokenName: string
}

export class SendTransactionError extends Error {
  logs: string[] | undefined

  constructor(message: string, logs?: string[]) {
    super(message)
    this.logs = logs
  }
}

export interface TotalValues {
  totalBorrowUsd: string
  totalSupplyUsd: string
  totalAvailableUsd: string
  totalMarket: string
}
